import React, { useState, useEffect } from 'react'; // Keep only one import
import { useParams } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import PrintableContract from '../printable/PrintableContract';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Define the expected structure of the fetched contract data
// Match the updated ClientInfo in PrintableContract (though clientRequisites is primary now)
interface ClientInfo {
    name: string;
    legalName?: string | null;
    basisOfAuthority?: string | null;
    director?: string | null;
    address?: string | null;
    phone?: string | null;
    email?: string | null;
    taxId?: string | null; // БИН/ИИН
    bankAccount?: string | null; // ИИК
    bankName?: string | null; // Название банка
    bankBik?: string | null; // БИК
    kbe?: string | null;
    okpo?: string | null;
    treasuryCommittee?: string | null;
}

interface ContractItem {
    structureCode: string;
    structureType: string;
    location: string;
    startDate: Date | string;
    endDate: Date | string;
    unitPrice: number;
    quantity: number;
    finalPrice: number;
}

// Add Requisites interface (matching ContractDetails/Form)
interface Requisites {
    name?: string;
    address?: string;
    bin?: string;
    iik?: string;
    bankName?: string;
    bik?: string;
    phone?: string;
    email?: string;
    basisOfAuthority?: string;
    director?: string;
}


interface ContractData {
    contractNumber: string;
    signingDate: Date | string;
    signingCity?: string | null;
    startDate: Date | string;
    endDate?: Date | string | null;
    totalAmount: number;
    paymentTerms?: string | null;
    client: ClientInfo; // Keep for fallback/basic info if needed
    items: ContractItem[];
    // Use Requisites type
    companyRequisites?: Requisites;
    clientRequisites?: Requisites;
    // Add other fields from backend if needed
    termsAndConditions?: string | null;
    content?: string | null;
    title?: string | null;
    issueDate?: Date | string | null;
}

const ContractPrintPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [contractData, setContractData] = useState<ContractData | null>(null);
    const [loadingContract, setLoadingContract] = useState(true);
    const [errorContract, setErrorContract] = useState<string | null>(null);

    // Remove useSettings hook and agencyDetails state

    useEffect(() => {
        const fetchContractData = async () => {
            setLoadingContract(true); // Use renamed state
            setErrorContract(null); // Use renamed state
            try {
                // Use the backend endpoint to get contract data including client details
                const response = await axios.get<ContractData>(`/contracts/${id}/print-data`);
                setContractData(response.data);
                // Attempt to trigger print dialog after data is loaded
                setTimeout(() => {
                    // window.print();
                }, 500); // Small delay to allow rendering
            } catch (err: any) {
                console.error('Error fetching contract data for printing:', err);
                setErrorContract(err.response?.data?.message || 'Не удалось загрузить данные договора для печати.'); // Use renamed state
            } finally {
                setLoadingContract(false); // Use renamed state
            }
        };

        if (id) {
            fetchContractData();
        } else {
            setErrorContract('ID договора не найден.'); // Use renamed state
            setLoadingContract(false); // Use renamed state
        }
    }, [id]);

    if (loadingContract) { // Only check contract loading state
        return <LoadingSpinner />;
    }

    if (errorContract) { // Only check contract error state
        return <div className="printable-container error-message">Ошибка: {errorContract}</div>;
    }

    if (!contractData) { // Only check contract data object
        return <div className="printable-container">Данные для печати не найдены.</div>;
    }

    // Render the printable component, remove agencyDetails prop
    return <PrintableContract contractData={contractData} />;
};

export default ContractPrintPage;
